import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useWritesRefused } from '../components/SuspendedNotice.js';
import { Alert, Card } from '../components/ui.js';
import { fetchClientProfile, type ClientProfile } from '../lib/ordersApi.js';

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-sm text-fg-muted">{label}</dt>
      <dd className="mt-0.5 text-sm whitespace-pre-line text-fg">{value}</dd>
    </div>
  );
}

function List({ values, empty }: { values: string[]; empty: string }) {
  if (values.length === 0) return <span className="text-fg-faint">{empty}</span>;
  return <>{values.join(', ')}</>;
}

export function ClientDetailPage(): React.JSX.Element {
  // Suspended, or an operator viewing read-only - WAIT-B defect 3.
  const { refused: suspended } = useWritesRefused();
  const { clientProfileId } = useParams<{ clientProfileId: string }>();
  const [client, setClient] = useState<ClientProfile | null>(null);
  const [orderCount, setOrderCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!clientProfileId) return;
    let cancelled = false;
    void (async () => {
      try {
        const response = await fetchClientProfile(clientProfileId);
        if (cancelled) return;
        setClient(response.clientProfile);
        setOrderCount(response.orderCount);
      } catch {
        if (!cancelled) setError('We could not find that project.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientProfileId]);

  if (loading) {
    return (
      <p className="text-sm text-fg-muted" role="status">
        Loading…
      </p>
    );
  }

  if (error || !client) {
    return <Alert>{error ?? 'We could not find that project.'}</Alert>;
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to="/projects" className="text-sm text-fg-muted underline">
          Back to projects
        </Link>
        <div className="mt-2 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{client.businessName}</h1>
            <p className="mt-1 text-sm text-fg-muted">
              {orderCount === 0
                ? 'No orders yet'
                : `${orderCount} order${orderCount === 1 ? '' : 's'}`}
            </p>
          </div>
          <div className="flex shrink-0 gap-3">
            <Link
              to={`/projects/${client.id}/edit`}
              className="rounded-control bg-card px-4 py-2.5 text-sm font-semibold text-fg ring-1 ring-inset ring-rule-strong transition hover:bg-subtle"
            >
              Edit
            </Link>
            {suspended ? null : (
            <Link
              to={`/orders/new?projectId=${client.id}`}
              className="rounded-control bg-blue px-4 py-2.5 text-sm font-semibold text-ground transition hover:bg-blue-bright"
            >
              Place an order
            </Link>
            )}
          </div>
        </div>
      </div>

      <Card title="Target information">
        <dl className="mt-4 grid gap-4 sm:grid-cols-2">
          <Detail
            label="Main URL"
            value={client.websiteUrl ?? <span className="text-fg-faint">Not set</span>}
          />
          <Detail
            label="Target location"
            value={client.targetLocation ?? <span className="text-fg-faint">Not set</span>}
          />
        </dl>
      </Card>

      <Card title="Sitewide footer">
        {client.footerNap ? (
          <dl className="mt-4 grid gap-4 sm:grid-cols-2">
            <Detail label="Business name" value={orNotSet(client.footerNap.businessName)} />
            <Detail label="Phone" value={orNotSet(client.footerNap.phone)} />
            <Detail label="Address" value={addressLines(client.footerNap)} />
            <Detail label="Map" value={orNotSet(client.footerNap.mapsEmbedUrl)} />
          </dl>
        ) : (
          <p className="mt-3 text-sm text-fg-muted">No footer details on this project.</p>
        )}
      </Card>

      <LegacyDetails client={client} />
    </div>
  );
}

function orNotSet(value: string | null): React.ReactNode {
  return value ?? <span className="text-fg-faint">Not set</span>;
}

function addressLines(footer: NonNullable<ClientProfile['footerNap']>): React.ReactNode {
  const lines = [
    footer.addressLine1,
    footer.addressLine2,
    footer.city,
    footer.region,
    footer.postcode,
    footer.country,
  ].filter((line): line is string => line !== null && line !== '');

  return lines.length > 0 ? lines.join('\n') : <span className="text-fg-faint">Not set</span>;
}

/**
 * Details from before intake v2, shown only when a project actually has some.
 *
 * These fields are no longer asked for and no longer editable here — the
 * operator ruled they are not fulfilment inputs. Their columns were kept, so
 * hiding what they hold would make stored data invisible rather than absent.
 * A project created since intake v2 has none of this and shows nothing.
 */
function LegacyDetails({ client }: { client: ClientProfile }): React.JSX.Element | null {
  const extraLocations = client.locations.slice(1);
  const has =
    client.services.length > 0 ||
    extraLocations.length > 0 ||
    client.targetQueries.length > 0 ||
    client.linkTargets.length > 0 ||
    Boolean(client.usps) ||
    Boolean(client.brandConstraints) ||
    Boolean(client.avoidNotes) ||
    Boolean(client.footerBlock) ||
    Boolean(client.gbpUrl);

  if (!has) return null;

  return (
    <Card title="Earlier details">
      <p className="mt-1 text-sm text-fg-muted">
        Kept from before the order form was simplified. These are not asked for any more, and
        orders already placed keep the details they were placed with.
      </p>
      <dl className="mt-4 grid gap-4 sm:grid-cols-2">
        {client.services.length > 0 ? (
          <Detail label="Services" value={<List values={client.services} empty="Not set" />} />
        ) : null}
        {extraLocations.length > 0 ? (
          <Detail
            label="Other locations"
            value={<List values={extraLocations} empty="Not set" />}
          />
        ) : null}
        {client.targetQueries.length > 0 ? (
          <Detail
            label="Searches to be found for"
            value={<List values={client.targetQueries} empty="Not set" />}
          />
        ) : null}
        {client.linkTargets.length > 0 ? (
          <Detail
            label="Pages to link to"
            value={
              <ul className="space-y-1">
                {client.linkTargets.map((target) => (
                  <li key={target.url}>
                    {target.url}
                    {target.anchor ? (
                      <span className="text-fg-muted">
                        {' '}
                        · anchor text: &ldquo;{target.anchor}&rdquo;
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            }
          />
        ) : null}
        {client.usps ? <Detail label="Unique selling points" value={client.usps} /> : null}
        {client.brandConstraints ? (
          <Detail label="Brand rules" value={client.brandConstraints} />
        ) : null}
        {client.avoidNotes ? <Detail label="Things to avoid" value={client.avoidNotes} /> : null}
        {client.footerBlock ? <Detail label="Footer text" value={client.footerBlock} /> : null}
        {client.gbpUrl ? <Detail label="Google Business Profile" value={client.gbpUrl} /> : null}
      </dl>
    </Card>
  );
}
